/**
 * Time formatting utilities for displaying relative time ("X time ago")
 * Supports both English and Arabic with proper RTL handling
 * Uses user's phone timezone for display, server stores UTC
 */

/**
 * Get user's timezone from browser
 * @returns {string} IANA timezone string (e.g., "Africa/Khartoum")
 */
export function getUserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * Format a date as a relative time string (e.g., "2 days ago", "قبل يومين")
 * Uses Intl.RelativeTimeFormat for proper localization
 * 
 * @param {string} dateISO - ISO date string (UTC)
 * @param {string} locale - Locale code ('en' or 'ar')
 * @returns {string} Formatted relative time string
 */
export function formatTimeAgo(dateISO, locale = "en") {
  if (!dateISO) return "";
  
  const date = new Date(dateISO);
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.max(1, Math.floor(diffMs / 1000));

  const units = [
    ["year", 31536000],
    ["month", 2592000],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
    ["second", 1],
  ];

  // Use Intl.RelativeTimeFormat for proper localization
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  for (const [unit, secondsInUnit] of units) {
    const value = Math.floor(diffSec / secondsInUnit);
    if (value >= 1) {
      return rtf.format(-value, unit); // negative = "X ago"
    }
  }
  return rtf.format(-1, "second");
}

/**
 * Format relative time in minutes/hours/days
 * Simpler version for quick display
 * 
 * @param {string} isoString - ISO date string (UTC)
 * @param {string} locale - Locale code ('en' or 'ar')
 * @returns {string} Formatted relative time
 */
export function formatRelativeMinutes(isoString, locale = "en") {
  if (!isoString) return "";
  const d = new Date(isoString);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60000);
  
  if (mins < 1) return locale.startsWith("ar") ? "الآن" : "Just now";
  if (mins < 60) return locale.startsWith("ar") ? `قبل ${mins} دقيقة` : `${mins}m ago`;
  
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return locale.startsWith("ar") ? `قبل ${hrs} ساعة` : `${hrs}h ago`;
  
  const days = Math.round(hrs / 24);
  return locale.startsWith("ar") ? `قبل ${days} يوم` : `${days}d ago`;
}

/**
 * Format a date as an exact date/time string in user's timezone
 * 
 * @param {string} dateISO - ISO date string (UTC from server)
 * @param {string} locale - Locale code ('en' or 'ar')
 * @param {string} timeZone - IANA timezone (defaults to user's browser timezone)
 * @returns {string} Formatted exact date string in user's timezone
 */
export function formatExactDate(dateISO, locale = "en", timeZone) {
  if (!dateISO) return "";
  
  const tz = timeZone || getUserTimeZone();
  const d = new Date(dateISO);
  
  return new Intl.DateTimeFormat(locale, {
    timeZone: tz,
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

/**
 * Format a date/time with full timezone-aware display
 * Best for transaction history, ad details, etc.
 * 
 * @param {string} isoString - ISO date string (UTC from server)
 * @param {string} locale - Locale code ('en' or 'ar')
 * @param {string} timeZone - IANA timezone (optional, defaults to browser)
 * @returns {string} Formatted date/time string
 */
export function formatDateTime(isoString, locale = "en", timeZone) {
  if (!isoString) return "";
  
  const tz = timeZone || getUserTimeZone();
  const d = new Date(isoString);
  
  return new Intl.DateTimeFormat(locale, {
    timeZone: tz,
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

/**
 * Format date only (no time) in user's timezone
 * 
 * @param {string} isoString - ISO date string (UTC from server)
 * @param {string} locale - Locale code ('en' or 'ar')
 * @param {string} timeZone - IANA timezone (optional)
 * @returns {string} Formatted date string
 */
export function formatDateOnly(isoString, locale = "en", timeZone) {
  if (!isoString) return "";
  
  const tz = timeZone || getUserTimeZone();
  const d = new Date(isoString);
  
  return new Intl.DateTimeFormat(locale, {
    timeZone: tz,
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(d);
}

/**
 * Format time only in user's timezone
 * 
 * @param {string} isoString - ISO date string (UTC from server)
 * @param {string} locale - Locale code ('en' or 'ar')
 * @param {string} timeZone - IANA timezone (optional)
 * @returns {string} Formatted time string
 */
export function formatTimeOnly(isoString, locale = "en", timeZone) {
  if (!isoString) return "";
  
  const tz = timeZone || getUserTimeZone();
  const d = new Date(isoString);
  
  return new Intl.DateTimeFormat(locale, {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

/**
 * Sync user's timezone to backend after login
 * Call this once per session after authentication
 * 
 * @param {string} token - Auth token
 * @param {string} apiUrl - Backend API URL
 */
export async function syncTimezoneToBackend(token, apiUrl) {
  if (!token) return;
  
  const tz = getUserTimeZone();
  try {
    await fetch(`${apiUrl}/api/users/me/timezone`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({ timezone: tz }),
      credentials: "include",
    });
  } catch (e) {
    console.debug("Timezone sync failed:", e);
  }
}

